import React from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import CheckBoxOutlineBlankRoundedIcon from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import CheckBoxRoundedIcon from '@mui/icons-material/CheckBoxRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import userService, { AppUser } from '../services/userService';
import {
  listUserTasksForView,
  createMyTask,
  completeMyTask,
  updateMyTask,
  type LocalTaskItem,
} from '../services/localTasksService';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../components/layouts/Layout';

const TaskerPage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [users, setUsers] = React.useState<Array<Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url'>>>([]);
  const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
  const [loadingUsers, setLoadingUsers] = React.useState(false);
  const [loadingTasks, setLoadingTasks] = React.useState(false);
  const [activeTasks, setActiveTasks] = React.useState<LocalTaskItem[]>([]);
  const [completedTasks, setCompletedTasks] = React.useState<LocalTaskItem[]>([]);
  const [taskQuery, setTaskQuery] = React.useState<string>('');
  const [composerVisible, setComposerVisible] = React.useState<boolean>(false);
  const [newTitle, setNewTitle] = React.useState<string>('');
  const [dueDate, setDueDate] = React.useState<string | null>(null);
  const dateInputRef = React.useRef<HTMLInputElement | null>(null);
  const { showNotification } = useNotification();
  const titleInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    if (!user?.user_id) return;
    if (!isAdmin) {
      setUsers([
        {
          user_id: user.user_id,
          display_name: user.display_name || user.email || '',
          email: user.email || '',
          avatar_url: user.avatar_url,
        },
      ]);
      setSelectedUserId(user.user_id);
      setLoadingUsers(false);
      return;
    }
    setLoadingUsers(true);
    userService
      .listUsersPublic()
      .then((u) => {
        setUsers(u);
        const me = u.find((x) => x.user_id === user?.user_id);
        setSelectedUserId(me ? me.user_id : u[0]?.user_id || null);
      })
      .finally(() => setLoadingUsers(false));
  }, [user?.user_id, user?.display_name, user?.email, user?.avatar_url, isAdmin]);

  const loadForUser = React.useCallback(
    async (uid: string) => {
      setLoadingTasks(true);
      try {
        const forUserId = isAdmin ? uid : undefined;
        const all = await listUserTasksForView({ showCompleted: true, forUserId });
        const activeFlat = all.filter((t) => t.status !== 'completed');
        const doneFlat = all.filter((t) => t.status === 'completed');
        setActiveTasks(activeFlat);
        setCompletedTasks(doneFlat);
      } catch (e: any) {
        const msg = e?.response?.data?.message || e?.message || 'Nie udało się wczytać zadań';
        showNotification(String(msg), 'error');
        setActiveTasks([]);
        setCompletedTasks([]);
      } finally {
        setLoadingTasks(false);
      }
    },
    [isAdmin, showNotification]
  );

  React.useEffect(() => {
    if (selectedUserId) loadForUser(selectedUserId);
  }, [selectedUserId, loadForUser]);

  const toIsoFromYmd = (ymd: string | null) => {
    if (!ymd) return undefined;
    try {
      const [y, m, d] = ymd.split('-').map((x) => parseInt(x, 10));
      const dt = new Date(y, (m || 1) - 1, d || 1, 9, 0, 0);
      return dt.toISOString();
    } catch {
      return undefined;
    }
  };

  const forUserIdForCreate = () => {
    if (!isAdmin || !selectedUserId || !user?.user_id) return undefined;
    return selectedUserId !== user.user_id ? selectedUserId : undefined;
  };

  const handleAddTask = async (overrideYmd?: string | null) => {
    if (!selectedUserId) return;
    const title = (newTitle || '').trim();
    if (!title) return;
    const ymd = typeof overrideYmd !== 'undefined' ? overrideYmd : dueDate;
    const assignerNote =
      selectedUserId && user?.user_id && selectedUserId !== user.user_id
        ? `__ASSIGNER__:${user.user_id}:${encodeURIComponent((user as any)?.display_name || (user as any)?.email || '')}:${encodeURIComponent((user as any)?.avatar_url || '')}`
        : undefined;
    try {
      await createMyTask({
        title,
        due: toIsoFromYmd(ymd) || null,
        ...(assignerNote ? { notes: assignerNote } : {}),
        forUserId: forUserIdForCreate(),
      });
      setNewTitle('');
      setDueDate(null);
      setComposerVisible(false);
      await loadForUser(selectedUserId);
      showNotification('Zadanie dodane', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Nie udało się dodać zadania';
      showNotification(String(msg), 'error');
    }
  };

  const markComplete = async (id: string) => {
    try {
      await completeMyTask(id);
      if (selectedUserId) await loadForUser(selectedUserId);
      showNotification('Oznaczono jako zrobione', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Błąd';
      showNotification(String(msg), 'error');
    }
  };

  const markOpen = async (id: string) => {
    try {
      await updateMyTask(id, { status: 'needsAction' });
      if (selectedUserId) await loadForUser(selectedUserId);
      showNotification('Przywrócono do listy', 'success');
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Błąd';
      showNotification(String(msg), 'error');
    }
  };

  React.useEffect(() => {
    if (!composerVisible) return;
    const t = setTimeout(() => {
      try {
        titleInputRef.current?.focus();
      } catch {}
    }, 0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setComposerVisible(false);
        setNewTitle('');
        setDueDate(null);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('keydown', onKey, true as any);
    };
  }, [composerVisible]);

  const getAssigner = React.useCallback((notes?: string | null) => {
    if (!notes || typeof notes !== 'string') return null;
    if (!notes.startsWith('__ASSIGNER__')) return null;
    try {
      const parts = notes.split(':');
      const uid = parts[1] || '';
      const name = parts[2] ? decodeURIComponent(parts[2]) : '';
      const avatar = parts[3] ? decodeURIComponent(parts[3]) : '';
      return { userId: uid, name, avatar };
    } catch {
      return null;
    }
  }, []);

  const AssignerChip: React.FC<{ name: string; avatar?: string | null }> = ({ name }) => {
    const initials = React.useMemo(() => {
      const src = (name || '').trim();
      if (!src) return '?';
      const parts = src.split(/\s+/).filter(Boolean);
      const raw = (parts[0]?.[0] || '') + (parts[1]?.[0] || '');
      return raw.toUpperCase();
    }, [name]);
    return (
      <Tooltip title={`Od: ${name || '—'}`}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            border: '1px solid #d0d0d6',
            borderRadius: '999px',
            height: 22,
            px: 0.75,
            backgroundColor: '#F7F7F9',
            color: '#2D2D32',
            fontSize: 12,
            lineHeight: '16px',
            whiteSpace: 'nowrap',
          }}
        >
          <Box component="span">{`Od ${initials}`}</Box>
        </Box>
      </Tooltip>
    );
  };

  const paperMainSx = {
    p: 0,
    overflow: 'hidden',
    borderRadius: 2,
    border: '1px solid #E8E8ED',
    boxShadow: '0 1px 3px rgba(2, 2, 66, 0.06)',
  } as const;

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px', pb: 4, maxWidth: 1200, mx: 'auto', px: { xs: 1, sm: 0 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: '16.5px', mb: 0, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography component="h1" sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 22, lineHeight: '28px', fontWeight: 500, color: '#020242', m: 0 }}>
            Tasker
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 13, color: '#6B6B76', fontFamily: 'Roboto, sans-serif' }}>
            Zadania z Unitresco (MySQL). Bez Google Tasks.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: isAdmin ? { xs: '1fr', md: '280px 1fr' } : '1fr',
          gap: 2,
          mt: '24px',
          alignItems: 'start',
        }}
      >
        {isAdmin ? (
          <Paper variant="outlined" sx={paperMainSx}>
            <Box sx={{ p: 1.5, borderBottom: '1px solid #EDEDED', backgroundColor: '#FAFAFC' }}>
              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 13, fontWeight: 600, color: '#2D2D32' }}>Osoba</Typography>
              <Typography sx={{ fontSize: 12, color: '#6B6B76', mt: 0.25 }}>Admin: wybierz użytkownika</Typography>
            </Box>
            {loadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <List dense sx={{ py: 0, maxHeight: { md: 'min(70vh, 640px)' }, overflow: 'auto' }}>
                {users.map((u) => (
                  <React.Fragment key={u.user_id}>
                    <ListItemButton selected={selectedUserId === u.user_id} onClick={() => setSelectedUserId(u.user_id)} sx={{ py: 1 }}>
                      <ListItemAvatar>
                        <Avatar src={u.avatar_url || undefined}>{(u.display_name || u.email || '?').slice(0, 1)}</Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={<Typography sx={{ fontSize: 14, fontFamily: 'Roboto, sans-serif', color: '#2D2D32' }}>{u.display_name || u.email}</Typography>}
                        secondary={<Typography sx={{ fontSize: 12, color: '#6B6B76' }}>{u.email}</Typography>}
                      />
                    </ListItemButton>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>
            )}
          </Paper>
        ) : null}

        <Paper variant="outlined" sx={paperMainSx}>
          <Box
            sx={{
              p: 1.5,
              borderBottom: '1px solid #EDEDED',
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              backgroundColor: '#FAFAFC',
            }}
          >
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, fontWeight: 600, color: '#2D2D32', flex: '0 0 auto' }}>
              {isAdmin ? 'Zadania wybranej osoby' : 'Moje zadania'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { xs: 0, sm: 'auto' }, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                onClick={() => setComposerVisible(true)}
                disabled={!selectedUserId}
                sx={{ height: 36, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', px: 2 }}
              >
                Dodaj zadanie
              </Button>
              <TextField
                size="small"
                placeholder="Szukaj…"
                value={taskQuery}
                onChange={(e) => setTaskQuery(e.target.value)}
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Box
                        component="img"
                        src="/assets/filters/search.svg"
                        alt=""
                        onError={(e: any) => {
                          try {
                            e.currentTarget.src = '/assets/topbar/search.svg';
                          } catch {}
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: { xs: '100%', sm: 280 },
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                  '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                  '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
                  '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 },
                }}
              />
            </Box>
          </Box>
          {composerVisible && (
            <Box sx={{ px: 1.5, pt: 1, pb: 1.5, borderBottom: '1px solid #EDEDED', backgroundColor: '#fff' }}>
              <Stack spacing={1}>
                <TextField
                  placeholder="Nazwa zadania"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  fullWidth
                  size="small"
                  inputRef={titleInputRef}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTask(null);
                    }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setComposerVisible(false);
                      setNewTitle('');
                      setDueDate(null);
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': { backgroundColor: '#fff', borderRadius: '8px' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d4d4d9' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#595966' },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
                    '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
                    '& .MuiInputBase-input': { fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: newTitle ? '#2D2D32' : '#B9B9B9' },
                    '& .MuiInputBase-input::placeholder': { fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '16px', color: '#B9B9B9', opacity: 1 },
                  }}
                />
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  {(() => {
                    const today = new Date().toISOString().slice(0, 10);
                    const tomorrow = (() => {
                      const d = new Date();
                      d.setDate(d.getDate() + 1);
                      return d.toISOString().slice(0, 10);
                    })();
                    return (
                      <>
                        <Button
                          size="small"
                          variant={dueDate === today ? 'contained' : 'outlined'}
                          onClick={() => handleAddTask(today)}
                          sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 28 }}
                        >
                          Dzisiaj
                        </Button>
                        <Button
                          size="small"
                          variant={dueDate === tomorrow ? 'contained' : 'outlined'}
                          onClick={() => handleAddTask(tomorrow)}
                          sx={{ textTransform: 'none', borderRadius: '8px', minHeight: 28 }}
                        >
                          Jutro
                        </Button>
                      </>
                    );
                  })()}
                  <Tooltip title={dueDate ? `Termin: ${dueDate}` : 'Ustaw termin'}>
                    <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                      <Box
                        component="img"
                        src="/assets/filters/calendar_today.svg"
                        alt="calendar"
                        sx={{ width: 20, height: 20, cursor: 'pointer', opacity: 0.85 }}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          try {
                            (dateInputRef.current as any)?.showPicker?.();
                          } catch {
                            dateInputRef.current?.focus();
                            dateInputRef.current?.click();
                          }
                        }}
                      />
                      <input
                        ref={dateInputRef}
                        type="date"
                        value={dueDate || ''}
                        onChange={(e) => {
                          const value = e.target.value || '';
                          if (!value) return;
                          setDueDate(value);
                          handleAddTask(value);
                        }}
                        tabIndex={-1}
                        style={{ position: 'absolute', left: 0, top: 0, width: 1, height: 1, opacity: 0, border: 'none', background: 'transparent' }}
                      />
                    </Box>
                  </Tooltip>
                </Stack>
              </Stack>
            </Box>
          )}
          {loadingTasks ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 0,
                p: 0,
                minHeight: 400,
              }}
            >
              {(() => {
                const q = (taskQuery || '').toLowerCase();
                const active = activeTasks.filter(
                  (t) => !q || (t.title || '').toLowerCase().includes(q) || (t.notes || '').toLowerCase().includes(q)
                );
                const done = completedTasks.filter(
                  (t) => !q || (t.title || '').toLowerCase().includes(q) || (t.notes || '').toLowerCase().includes(q)
                );
                const colSx = {
                  borderRight: { xs: 'none', sm: '1px solid' },
                  borderColor: { sm: 'divider' },
                  borderBottom: { xs: '1px solid', sm: 'none' },
                  minHeight: { xs: 200, sm: 480 },
                  display: 'flex',
                  flexDirection: 'column',
                } as const;
                const headerSx = {
                  px: 2,
                  py: 1.25,
                  fontFamily: 'Roboto, sans-serif',
                  fontSize: 14,
                  fontWeight: 600,
                  color: '#020242',
                  backgroundColor: '#F5F5F8',
                  borderBottom: '1px solid #E8E8ED',
                } as const;
                const rowSx = {
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr 88px',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 1,
                  borderBottom: '1px solid #F0F0F3',
                  '&:last-of-type': { borderBottom: 'none' },
                } as const;
                const emptySx = { px: 2, py: 3, color: '#6B6B76', textAlign: 'center' } as const;
                return (
                  <>
                    <Box sx={colSx}>
                      <Box sx={headerSx}>Do zrobienia ({active.length})</Box>
                      <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {active.length === 0 ? (
                          <Typography sx={emptySx}>Brak otwartych zadań.</Typography>
                        ) : (
                          active.map((t) => (
                            <Box key={t.id} sx={rowSx}>
                              <IconButton size="small" onClick={() => markComplete(t.id)} aria-label="Oznacz jako zrobione" sx={{ color: '#3F3FDC' }}>
                                <CheckBoxOutlineBlankRoundedIcon fontSize="small" />
                              </IconButton>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <Typography sx={{ fontSize: 14, color: '#2D2D32', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</Typography>
                                {(() => {
                                  const a = getAssigner(t.notes);
                                  return a ? <AssignerChip name={a.name} avatar={a.avatar} /> : null;
                                })()}
                              </Box>
                              <Typography sx={{ fontSize: 12, color: '#6B6B76', textAlign: 'right' }}>{t.due ? String(t.due).slice(0, 10) : '—'}</Typography>
                            </Box>
                          ))
                        )}
                      </Box>
                    </Box>
                    <Box sx={{ ...colSx, borderRight: { sm: 'none' } }}>
                      <Box sx={headerSx}>Ukończone ({done.length})</Box>
                      <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {done.length === 0 ? (
                          <Typography sx={emptySx}>Brak ukończonych.</Typography>
                        ) : (
                          done.map((t) => (
                            <Box key={t.id} sx={rowSx}>
                              <IconButton size="small" onClick={() => markOpen(t.id)} aria-label="Przywróć" sx={{ color: '#3F3FDC' }}>
                                <UndoRoundedIcon fontSize="small" />
                              </IconButton>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                                <CheckBoxRoundedIcon sx={{ color: '#3F3FDC', fontSize: 20 }} />
                                <Typography
                                  sx={{
                                    fontSize: 14,
                                    color: '#2D2D32',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    textDecoration: 'line-through',
                                    opacity: 0.85,
                                  }}
                                >
                                  {t.title}
                                </Typography>
                                {(() => {
                                  const a = getAssigner(t.notes);
                                  return a ? <AssignerChip name={a.name} avatar={a.avatar} /> : null;
                                })()}
                              </Box>
                              <Typography sx={{ fontSize: 12, color: '#6B6B76', textAlign: 'right' }}>
                                {t.completed ? String(t.completed).slice(0, 10) : t.updated ? String(t.updated).slice(0, 10) : '—'}
                              </Typography>
                            </Box>
                          ))
                        )}
                      </Box>
                    </Box>
                  </>
                );
              })()}
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default TaskerPage;
